import React from "react";
import { useParams, Link } from "react-router-dom";
import { db } from "../lib/instant";
import { useAuth } from "../context/AuthContext";
import { Printer, ArrowLeft, Package, CheckCircle, Truck, Clock, XCircle } from "lucide-react";

const STATUS_STEPS = ["Pending Payment","Payment Received","Processing","Shipped","Delivered"];
const STATUS_CONFIG = {
  "Pending Payment":  { color:"#f57c00", bg:"#fff3e0", icon:<Clock size={16}/> },
  "Payment Received": { color:"#1976d2", bg:"#e3f2fd", icon:<CheckCircle size={16}/> },
  "Processing":       { color:"#7b1fa2", bg:"#f3e5f5", icon:<Package size={16}/> },
  "Shipped":          { color:"#0288d1", bg:"#e1f5fe", icon:<Truck size={16}/> },
  "Delivered":        { color:"#388e3c", bg:"#e8f5e9", icon:<CheckCircle size={16}/> },
  "Cancelled":        { color:"#d32f2f", bg:"#ffebee", icon:<XCircle size={16}/> },
};

export default function Invoice({ theme }) {
  const primaryColor   = theme?.primaryColor   || "#6b2118";
  const accentColor    = theme?.accentColor    || "#c9a84c";
  const secondaryColor = theme?.secondaryColor || "#2e7d32";
  const { id } = useParams();
  const { user } = useAuth();

  const { data, isLoading } = db.useQuery({ orders: { $: { where: { id } } }, settings: {} });
  const order    = data?.orders?.[0];
  const settings = data?.settings?.[0] || {};

  const handlePrint = () => window.print();

  if (isLoading) return (
    <div style={{ paddingTop: 100, textAlign:"center", color:"#aaa" }}>Loading order...</div>
  );
  if (!order) return (
    <div style={{ paddingTop:100, textAlign:"center" }}>
      <p style={{ color:"#aaa" }}>Order not found.</p>
      <Link to="/orders" style={{ color: primaryColor }}>← Back to Orders</Link>
    </div>
  );

  const items = (() => { try { return JSON.parse(order.items||"[]"); } catch { return []; } })();
  const statusConf = STATUS_CONFIG[order.status] || STATUS_CONFIG["Pending Payment"];
  const currentStep = STATUS_STEPS.indexOf(order.status);
  const isCancelled = order.status === "Cancelled";

  return (
    <div style={{ paddingTop: 64, minHeight:"100vh", background:"#fdf6e3" }}>
      {/* Actions bar — hidden on print */}
      <div className="no-print" style={{ background:"#fff", borderBottom:"1px solid #f0e8d0", padding:"12px 5vw" }}>
        <div style={{ maxWidth: 800, margin:"0 auto", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <Link to="/orders" style={{ display:"flex", alignItems:"center", gap:8, color: primaryColor, textDecoration:"none", fontWeight:700, fontSize:"0.88rem" }}>
            <ArrowLeft size={16}/> Back to Orders
          </Link>
          <button onClick={handlePrint} style={{ display:"flex", alignItems:"center", gap:8, background: primaryColor, color:"#fff", padding:"9px 20px", border:"none", borderRadius:10, fontWeight:700, cursor:"pointer", fontSize:"0.88rem" }}>
            <Printer size={15}/> Print Invoice
          </button>
        </div>
      </div>

      {/* Invoice */}
      <div style={{ maxWidth: 800, margin:"0 auto", padding:"40px 5vw" }}>
        <div id="invoice" style={{ background:"#fff", borderRadius: 20, overflow:"hidden", boxShadow:"0 4px 24px rgba(0,0,0,0.08)" }}>

          {/* Invoice header */}
          <div style={{ background: primaryColor, padding:"32px 36px", display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:16 }}>
            <div>
              <div style={{ fontFamily:"Georgia, serif", fontSize:"1.8rem", color:"#fff", fontWeight:800 }}>ACHU Health Mix</div>
              <div style={{ color: accentColor, fontSize:"0.75rem", letterSpacing:2, marginTop:2 }}>WE CARE FOR YOUR HEALTH</div>
              <div style={{ color:"rgba(255,255,255,0.65)", fontSize:"0.8rem", marginTop:8, lineHeight:1.6 }}>
                {settings.storeAddress || ""}<br/>
                📞 {settings.storePhone || "98403 20932"} · {settings.storePhone2 || "96590 01122"}<br/>
                🌐 www.achuhealthmix.com
              </div>
            </div>
            <div style={{ textAlign:"right" }}>
              <div style={{ fontSize:"0.72rem", color: accentColor, letterSpacing:2, textTransform:"uppercase", marginBottom:4 }}>Invoice / Order</div>
              <div style={{ fontFamily:"monospace", fontSize:"1.4rem", fontWeight:800, color:"#fff" }}>#{order.id?.slice(0,8).toUpperCase()}</div>
              <div style={{ color:"rgba(255,255,255,0.65)", fontSize:"0.82rem", marginTop:4 }}>
                {order.createdAt ? new Date(order.createdAt).toLocaleDateString("en-IN",{day:"numeric",month:"long",year:"numeric"}) : ""}
              </div>
              <div style={{ marginTop: 10 }}>
                <span style={{ background: statusConf.bg, color: statusConf.color, padding:"5px 14px", borderRadius:20, fontSize:"0.78rem", fontWeight:800 }}>
                  {order.status}
                </span>
              </div>
            </div>
          </div>

          {/* Order tracking steps */}
          {!isCancelled && (
            <div style={{ padding:"24px 36px", borderBottom:"1px solid #f0e8d0", background:"#fafafa" }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", position:"relative" }}>
                {/* connector line */}
                <div style={{ position:"absolute", top:18, left:"10%", right:"10%", height:2, background:"#e0e0e0", zIndex:0 }} />
                <div style={{ position:"absolute", top:18, left:"10%", width: currentStep >= 0 ? `${Math.min(currentStep / (STATUS_STEPS.length-1) * 80, 80)}%` : "0%", height:2, background: secondaryColor, zIndex:1, transition:"width 0.5s" }} />
                {STATUS_STEPS.map((s, i) => {
                  const done   = i <= currentStep;
                  const active = i === currentStep;
                  return (
                    <div key={s} style={{ textAlign:"center", position:"relative", zIndex:2, flex:1 }}>
                      <div style={{ width:36, height:36, borderRadius:"50%", margin:"0 auto 8px", display:"flex", alignItems:"center", justifyContent:"center", background: done ? secondaryColor : "#f0f0f0", border:`3px solid ${done ? secondaryColor : "#e0e0e0"}`, transition:"all 0.3s" }}>
                        {done ? <CheckCircle size={16} color="#fff"/> : <div style={{ width:10, height:10, borderRadius:"50%", background:"#ccc" }} />}
                      </div>
                      <div style={{ fontSize:"0.65rem", fontWeight: active ? 700 : 400, color: done ? secondaryColor : "#aaa", lineHeight:1.3 }}>{s.replace(" ","\n")}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div style={{ padding:"28px 36px" }}>
            {/* Bill to */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:24, marginBottom:28 }}>
              <div>
                <div style={{ fontSize:"0.72rem", fontWeight:700, color:"#888", letterSpacing:1.5, textTransform:"uppercase", marginBottom:10 }}>Bill To</div>
                <div style={{ fontWeight:700, color:"#1a1a1a", marginBottom:2 }}>{order.customerName || "—"}</div>
                <div style={{ fontSize:"0.85rem", color:"#666", lineHeight:1.7 }}>
                  📞 {order.phone || "—"}<br/>
                  ✉️ {order.userEmail || "—"}
                </div>
              </div>
              <div>
                <div style={{ fontSize:"0.72rem", fontWeight:700, color:"#888", letterSpacing:1.5, textTransform:"uppercase", marginBottom:10 }}>Deliver To</div>
                <div style={{ fontSize:"0.85rem", color:"#666", lineHeight:1.7 }}>{order.address || "—"}</div>
                {order.notes && <div style={{ fontSize:"0.78rem", color:"#aaa", marginTop:4, fontStyle:"italic" }}>Note: {order.notes}</div>}
              </div>
            </div>

            {/* Items table */}
            <div style={{ marginBottom:24 }}>
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead>
                  <tr style={{ background:"#f9f5f0" }}>
                    {["Item","Weight","Qty","Unit Price","Amount"].map(h => (
                      <th key={h} style={{ padding:"10px 14px", textAlign: h==="Amount"||h==="Qty"||h==="Unit Price"?"right":"left", fontSize:"0.72rem", fontWeight:700, color:"#888", letterSpacing:0.5, textTransform:"uppercase", borderBottom:"2px solid #f0e8d0" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, i) => (
                    <tr key={i} style={{ borderBottom:"1px solid #f5f5f5" }}>
                      <td style={{ padding:"12px 14px", fontWeight:600, color:"#1a1a1a", fontSize:"0.9rem" }}>{item.name}</td>
                      <td style={{ padding:"12px 14px", color:"#888", fontSize:"0.85rem" }}>{item.weight||"—"}</td>
                      <td style={{ padding:"12px 14px", textAlign:"right", color:"#555", fontSize:"0.9rem" }}>{item.quantity}</td>
                      <td style={{ padding:"12px 14px", textAlign:"right", color:"#555", fontSize:"0.9rem" }}>₹{Number(item.price).toFixed(2)}</td>
                      <td style={{ padding:"12px 14px", textAlign:"right", fontWeight:700, color: primaryColor, fontSize:"0.9rem" }}>₹{(item.price*item.quantity).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div style={{ display:"flex", justifyContent:"flex-end" }}>
              <div style={{ width:280 }}>
                {[
                  ["Subtotal", `₹${Number(order.subtotal||0).toFixed(2)}`],
                  [`GST (${order.gstRate||5}%)`, `₹${Number(order.gstAmount||0).toFixed(2)}`],
                  ["Delivery", Number(order.deliveryFee||0)===0 ? "FREE" : `₹${Number(order.deliveryFee).toFixed(2)}`],
                  ...(order.couponDiscount>0 ? [[`Coupon (${order.couponCode})`, `-₹${Number(order.couponDiscount).toFixed(2)}`]] : []),
                ].map(([l, v]) => (
                  <div key={l} style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", fontSize:"0.88rem", color:"#555", borderBottom:"1px solid #f5f5f5" }}>
                    <span>{l}</span>
                    <span style={{ fontWeight:600, color: v==="FREE"?secondaryColor : v.startsWith("-")?"#388e3c":"#333" }}>{v}</span>
                  </div>
                ))}
                <div style={{ display:"flex", justifyContent:"space-between", padding:"14px 0 0", fontSize:"1.1rem", fontWeight:800, color:primaryColor, borderTop:`2px solid ${primaryColor}`, marginTop:4 }}>
                  <span>Total Amount</span>
                  <span>₹{Number(order.total||0).toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Payment info */}
            <div style={{ marginTop:28, padding:16, background:"#f9f5f0", borderRadius:12, display:"flex", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
              <div>
                <div style={{ fontSize:"0.72rem", fontWeight:700, color:"#888", marginBottom:4, textTransform:"uppercase", letterSpacing:0.5 }}>Payment Method</div>
                <div style={{ fontWeight:600, color:"#333" }}>{order.paymentMethod || "GPay / UPI"}</div>
              </div>
              <div>
                <div style={{ fontSize:"0.72rem", fontWeight:700, color:"#888", marginBottom:4, textTransform:"uppercase", letterSpacing:0.5 }}>Order Date</div>
                <div style={{ fontWeight:600, color:"#333" }}>{order.createdAt ? new Date(order.createdAt).toLocaleDateString("en-IN",{weekday:"long",day:"numeric",month:"long",year:"numeric"}) : "—"}</div>
              </div>
              <div>
                <div style={{ fontSize:"0.72rem", fontWeight:700, color:"#888", marginBottom:4, textTransform:"uppercase", letterSpacing:0.5 }}>Order ID</div>
                <div style={{ fontWeight:700, color: primaryColor, fontFamily:"monospace" }}>#{order.id?.slice(0,8).toUpperCase()}</div>
              </div>
            </div>

            {/* Thank you */}
            <div style={{ textAlign:"center", marginTop:32, paddingTop:28, borderTop:"2px dashed #f0e8d0" }}>
              <div style={{ fontFamily:"cursive", fontSize:"1.4rem", color: primaryColor, marginBottom:4 }}>Thank You for Your Order!</div>
              <div style={{ fontSize:"0.82rem", color:"#aaa" }}>For support, call 98403 20932 or WhatsApp us.</div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          .no-print { display:none!important; }
          body { background:#fff!important; }
          #invoice { box-shadow:none!important; border-radius:0!important; }
        }
      `}</style>
    </div>
  );
}
